import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import env from '@jwp/ott-common/src/env';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import {
  GET_REPORTS_QUEUE_COUNTS,
  GetReportsQueueCountsData,
  GetReportsQueueCountsVariables,
  GET_REPORTS_QUEUE,
  GetReportsQueueData,
  GetReportsQueueVariables,
  ReportableType,
} from '@jwp/ott-common/src/graphql/query/reportModeration.queries';
import { startOfDay, formatToLocalDateTime, formatDateMMMddYYYY, subDays } from '@jwp/ott-common/src/utils/datetime';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import IllustrationIcon from '@jwp/ott-theme/assets/icons/illustration.svg?react';
import { PATH_HOME, PATH_REPORTS_DETAILS } from '@jwp/ott-common/src/paths';
import { useReportsStore } from '@jwp/ott-common/src/stores/ReportsStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import IconButton from '../IconButton/IconButton';
import Skeleton from '../Skeleton/Skeleton';
import styles from './ReportsQueueList.module.scss';
import ReportsQueueListFilter, { DateRangeType } from './ReportsQueueListFilter';
import { PaginationControls } from '../PaginationControls/PaginationControls';

const NON_REREVIEWED = ['Reported Content', 'Content Type', 'Reports Count', 'Last Reported Date'] as const;
const REREVIEWED = ['Reported Content', 'Content Type', 'Reports Count', 'Action Taken', 'Taken On'] as const;

function getDateRangeParams(range: DateRangeType) {
  const now = new Date();
  switch (range) {
    case 'today':
      return {
        startDate: formatToLocalDateTime(startOfDay(now)),
        endDate: formatToLocalDateTime(now),
      };
    case 'lastWeek':
      return {
        startDate: formatToLocalDateTime(subDays(now, 7)),
        endDate: formatToLocalDateTime(now),
      };
    case 'lastMonth':
      return {
        startDate: formatToLocalDateTime(subDays(now, 30)),
        endDate: formatToLocalDateTime(now),
      };
    case 'oldest':
    default:
      return {
        startDate: undefined,
        endDate: undefined,
      };
  }
}

const TAB_KEYS = ['reports', 'in_review', 'reviewed'] as const;
type ReportsQueueTabKey = (typeof TAB_KEYS)[number];

const ReportsQueueList: React.FC = () => {
  const { t } = useTranslation(['user', 'video', 'common', 'upload']);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAccountStore((state) => state.user);
  const { isModerateContentEnabled } = useAppConfig();
  const DEFAULT_CHANNEL_AVATAR = '/static/default-avatar.png';
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [activeTab, setActiveTab] = useState<ReportsQueueTabKey>('reports');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const { filters, setFilters } = useReportsStore();
  const { dateRange: appliedDateRange, contentTypes: appliedContentTypes = [] } = filters;

  const baseVariables = useMemo(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    const { startDate, endDate } = getDateRangeParams(appliedDateRange);

    return {
      offset,
      limit: itemsPerPage,
      startDate,
      endDate,
      reportableType: appliedContentTypes.length > 0 ? appliedContentTypes : undefined,
    };
  }, [currentPage, itemsPerPage, appliedDateRange, appliedContentTypes]);

  const { data: openData, loading: openLoading } = useQuery<GetReportsQueueData, GetReportsQueueVariables>(
    GET_REPORTS_QUEUE,
    {
      client: getAuthApolloClient(),
      variables: {
        input: {
          status: 'OPEN',
          ...baseVariables,
        },
      },
      skip: activeTab !== 'reports',
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: inReviewData, loading: inReviewLoading } = useQuery<GetReportsQueueData, GetReportsQueueVariables>(
    GET_REPORTS_QUEUE,
    {
      client: getAuthApolloClient(),
      variables: { input: { status: 'IN_REVIEW', ...baseVariables } },
      skip: activeTab !== 'in_review',
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: resolvedData, loading: resolvedLoading } = useQuery<GetReportsQueueData, GetReportsQueueVariables>(
    GET_REPORTS_QUEUE,
    {
      client: getAuthApolloClient(),
      variables: { input: { status: 'RESOLVED', ...baseVariables } },
      skip: activeTab !== 'reviewed',
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: reportsCountsData } = useQuery<GetReportsQueueCountsData, GetReportsQueueCountsVariables>(
    GET_REPORTS_QUEUE_COUNTS,
    {
      client: getAuthApolloClient(),
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    const tab = queryParams.get('tab');
    if (tab === 'reports' || tab === 'in_review' || tab === 'reviewed') {
      setActiveTab(tab);
    }
  }, [queryParams]);

  useEffect(() => {
    if (!openLoading && (!isModerateContentEnabled || !hasPermission(user, PERMISSIONS.APPROVALS_MEDIA))) {
      navigate(PATH_HOME, { replace: true });
    }
  }, [isModerateContentEnabled, user, openLoading, navigate]);

  const handleBackClick = () => {
    navigate(PATH_HOME);
  };

  const handleTabChange = (tab: ReportsQueueTabKey) => {
    setActiveTab(tab);
    setCurrentPage(1);
    const newParams = new URLSearchParams(location.search);
    newParams.delete('search');
    newParams.set('tab', tab);
    window.history.replaceState(
      null,
      '',
      `${location.pathname}?${newParams.toString()}`
    );
  };

  const handleApplyFilters = (dateRange: DateRangeType, contentTypes: ReportableType[]) => {
    setFilters({ dateRange, contentTypes });
    setCurrentPage(1);
  };

  const activeQueryMap = useMemo(() => {
    switch (activeTab) {
      case 'in_review':
        return { data: inReviewData, loading: inReviewLoading };
      case 'reviewed':
        return { data: resolvedData, loading: resolvedLoading };
      case 'reports':
      default:
        return { data: openData, loading: openLoading };
    }
  }, [activeTab, openData, openLoading, inReviewData, inReviewLoading, resolvedData, resolvedLoading]);

  const items = activeQueryMap.data?.getReportsQueue?.items || [];
  const totalCount = activeQueryMap.data?.getReportsQueue?.totalCount || 0;
  const loading = activeQueryMap.loading;

  const queueCounts = reportsCountsData?.getReportsQueueCounts;
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const isEmpty = !loading && items.length === 0;

  const thumbnailUrl = (url?: string | null): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    return `${env.APP_BASE_URL || ''}${url}`;
  };

  const getStatusClass = (maStatus?: string) => {
    switch (maStatus) {
      case 'REMOVE_CONTENT':
        return styles['content-removed'];
      case 'WARN_CREATOR':
        return styles['warned-creator'];
      case 'SUSPEND_CHANNEL':
        return styles['channel-suspended'];
      case 'DELETE_CHANNEL':
        return styles['channel-delete'];
      default:
        return '';
    }
  };

  const handleNavigateToDetails = (rcId: string) => {
    const fullPath = `${location.pathname}?tab=${activeTab}`;

    navigate(
      `${PATH_REPORTS_DETAILS}/${rcId}?returnPath=${encodeURIComponent(fullPath)}`
    );
  };

  const renderRows = () => {
    if (loading) {
      return Array.from({ length: 5 }).map((_, idx) => (
        <tr key={`sk-${idx}`}>
          <td>
            <div className={styles.thumbnailCell}>
              <Skeleton width="30px" height="52px" borderRadius="4px" />
              <Skeleton width="120px" height="16px" />
            </div>
          </td>
          <td>
            <Skeleton width="100px" height="16px" />
          </td>
          <td>
            <Skeleton width="60px" height="16px" />
          </td>
          <td>
            <Skeleton width="50px" height="16px" />
          </td>
        </tr>
      ));
    }

    return items.map((item) => {
      const contentTypeMap: Record<ReportableType, string> = {
        [ReportableType.REEL]: 'Reel',
        [ReportableType.VIDEO]: 'Video',
        [ReportableType.CHANNEL]: 'Channel',
      };
      const contentType = contentTypeMap[item.reportableType] || 'Video';

      return (
        <tr key={item.rcId}>
          <td>
            <div
              className={styles.thumbnailCell}
              role='button'
              onClick={() => handleNavigateToDetails(item.rcId)}
            >
              <img
                src={item.thumbnailUrl ? thumbnailUrl(item.thumbnailUrl) : DEFAULT_CHANNEL_AVATAR}
                alt={item.title}
                className={classNames(styles.thumb, styles[item.reportableType.toLowerCase()])}
              />
              <span className={styles.title}>{item.title || '-'}</span>
            </div>
          </td>
          <td>
            <span className={styles.contentType}>{contentType}</span>
          </td>
          <td>
            <span className={styles.statusText}>{item.reportsCount || '-'}</span>
          </td>
          {activeTab === 'reviewed' && (
            <>
              <td>
                <button
                  type="button"
                  className={`${styles['status-button']} ${getStatusClass(item?.action?.actionType)}`}
                >
                  {item?.action?.actionLabel || '—'}
                </button>
              </td>
              <td>
                <span className={styles.date}>{formatDateMMMddYYYY(item.action?.takenOn)}</span>
              </td>
            </>
          )}
          {activeTab !== 'reviewed' && (
            <td>
              <span className={styles.date}>{formatDateMMMddYYYY(item.lastReportedAt)}</span>
            </td>
          )}
        </tr>
      );
    });
  };

  const TABLE_COLUMNS = activeTab === 'reviewed' ? REREVIEWED : NON_REREVIEWED;

  const renderTableHead = () => (
    <thead>
      <tr>
        {TABLE_COLUMNS.map((column) => (
          <th key={column}>{column}</th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="Back" />
          </IconButton>
          <h1>{t('user:reports.report_queue_title')}</h1>
        </div>
      </header>
      <div className={styles.toolbarHeader}>
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {TAB_KEYS.map((tab) => {
              const counts = {
                reports: queueCounts?.open || 0,
                in_review: queueCounts?.inReview || 0,
                reviewed: queueCounts?.resolved || 0,
              };
              return (
                <button
                  key={tab}
                  type="button"
                  className={classNames(styles.tab, { [styles.activeTab]: activeTab === tab })}
                  onClick={() => handleTabChange(tab)}
                >
                  {t(`user:reports.tabs.${tab}`)}
                  <span className={styles.tabCount}>({counts[tab]})</span>
                </button>
              );
            })}
          </div>
        </div>
        <ReportsQueueListFilter
          className={styles.headerFilter}
          appliedDateRange={appliedDateRange}
          appliedContentTypes={appliedContentTypes}
          onApply={handleApplyFilters}
        />
      </div>
      <div className={classNames(styles.tableWrapper, { [styles.tableWrapperEmpty]: isEmpty })}>
        {isEmpty ? (
          <>
            <div className={styles.tableHeader}>
              <table className={styles.table}>{renderTableHead()}</table>
            </div>
            <div className={styles.emptyState}>
              <IllustrationIcon className={styles.emptyStateIcon} aria-hidden="true" />
              <p className={styles.emptyStateText}>{t('user:reports.report_filter.no_results_found')}</p>
            </div>
          </>
        ) : (
          <div className={styles.tableHorizontalScroll}>
            <div className={styles.tableInner}>
              <div className={styles.tableHeader}>
                <table className={styles.table}>{renderTableHead()}</table>
              </div>
              <div>
                <table className={styles.table}>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {!isEmpty && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(items) => {
              setItemsPerPage(items);
              setCurrentPage(1);
            }}
            loading={loading}
            className={styles.paginationControls}
          />
        )}
      </div>
    </div>
  );
};

export default ReportsQueueList;